// ---------------------------------------------------------------------------
// Report Builder — shared types, field catalog, option lists, palette,
// formatting helpers, and starter templates (GuardAI edition).
//
// Data sources map to GuardAI's Neon schema:
//   scans       → scan_events
//   hospitals   → hospitals
//   checkpoints → locations
//   devices     → scanners
//   guards      → guards
// ---------------------------------------------------------------------------

export type SourceKey = "scans" | "hospitals" | "checkpoints" | "devices" | "guards" | "officers";

export type WidgetType =
  | "metric"
  | "kpi_row"
  | "bar"
  | "line"
  | "pie"
  | "table"
  | "text"
  | "day_of_week"
  | "comparison"
  | "status_table"
  | "image"
  | "ai_summary";

export type WidgetSize = "small" | "medium" | "large";

export type SortKey = "value_desc" | "value_asc" | "label_asc" | "label_desc";

export type ColorKey = "blue" | "indigo" | "cyan" | "emerald" | "amber" | "red" | "slate" | "purple";

export interface WidgetFilters {
  hospital_id?: string;
  floor?: string;
  [key: string]: string | number | undefined;
}

export interface KpiItem {
  label: string;
  source: SourceKey;
  metric: string;
}

export interface WidgetConfig {
  id: string;
  type: WidgetType;
  title: string;
  size: WidgetSize;
  // data query
  source: SourceKey;
  metric: string;
  groupBy: string | null;
  filters: WidgetFilters;
  sort: SortKey;
  limit: number;
  // visualization options
  orientation: "horizontal" | "vertical";
  donut: boolean;
  color: ColorKey;
  showValues: boolean;
  // text / ai
  text: string;
  // image
  imageUrl: string | null;
  caption: string;
  // kpi row
  metrics: KpiItem[];
  // comparison (second series)
  seriesB: { label: string; source: SourceKey; metric: string } | null;
}

export interface ReportConfig {
  title: string;
  subtitle: string;
  author: string;
  dateRange: { start?: string; end?: string };
  includeLogo: boolean;
  widgets: WidgetConfig[];
}

// ---------------------------------------------------------------------------
// Palette (hex for SVG charts; blue-centric to match the GuardAI dashboard).
// ---------------------------------------------------------------------------
export const PALETTE: Record<ColorKey, string> = {
  blue: "#2563eb",
  indigo: "#4f46e5",
  cyan: "#0891b2",
  emerald: "#10b981",
  amber: "#f59e0b",
  red: "#ef4444",
  slate: "#475569",
  purple: "#8b5cf6",
};

/** Ordered colours for multi-series charts (pie slices, grouped bars). */
export const SERIES_COLORS: string[] = [
  PALETTE.blue,
  PALETTE.indigo,
  PALETTE.cyan,
  PALETTE.emerald,
  PALETTE.amber,
  PALETTE.red,
  PALETTE.slate,
  PALETTE.purple,
  "#60a5fa",
  "#a78bfa",
];

export const COLOR_OPTIONS: { key: ColorKey; label: string }[] = [
  { key: "blue", label: "Blue" },
  { key: "indigo", label: "Indigo" },
  { key: "cyan", label: "Cyan" },
  { key: "emerald", label: "Green" },
  { key: "amber", label: "Amber" },
  { key: "red", label: "Red" },
  { key: "slate", label: "Slate" },
  { key: "purple", label: "Purple" },
];

// ---------------------------------------------------------------------------
// Metric + dimension option lists (mirror the backend whitelist in
// api/reports/data.ts). Used by the configuration panel dropdowns.
// ---------------------------------------------------------------------------
interface MetricOpt {
  key: string;
  label: string;
  format: "int" | "decimal";
}

export const METRICS_BY_SOURCE: Record<SourceKey, MetricOpt[]> = {
  scans: [
    { key: "count", label: "Scan count", format: "int" },
    { key: "verified", label: "Verified scans", format: "int" },
    { key: "flagged", label: "Flagged scans", format: "int" },
    { key: "daily_average", label: "Daily average", format: "decimal" },
    { key: "attributed", label: "Officer-attributed scans", format: "int" },
    { key: "unattributed", label: "Unattributed scans", format: "int" },
  ],
  hospitals: [{ key: "count", label: "Hospital count", format: "int" }],
  checkpoints: [
    { key: "count", label: "Checkpoint count", format: "int" },
    { key: "scanned", label: "Scanned checkpoints", format: "int" },
    { key: "unscanned", label: "Unscanned checkpoints", format: "int" },
  ],
  devices: [
    { key: "count", label: "Device count", format: "int" },
    { key: "active", label: "Active devices", format: "int" },
  ],
  guards: [
    { key: "count", label: "Guard count", format: "int" },
    { key: "admins", label: "Admins", format: "int" },
  ],
  officers: [
    { key: "count", label: "Officer count", format: "int" },
    { key: "active", label: "Active officers", format: "int" },
  ],
};

interface DimOpt {
  key: string;
  label: string;
}

const TIME_DIMS: DimOpt[] = [
  { key: "hour", label: "Hour of day" },
  { key: "day_of_week", label: "Day of week" },
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
];

const CATEGORICAL_DIMS: Record<SourceKey, DimOpt[]> = {
  scans: [
    { key: "hospital", label: "Hospital" },
    { key: "floor", label: "Floor" },
    { key: "building", label: "Building" },
    { key: "location", label: "Checkpoint" },
    { key: "scanner", label: "Scanner" },
    { key: "guard", label: "Guard" },
    { key: "officer", label: "Officer" },
  ],
  hospitals: [{ key: "hospital", label: "Hospital" }],
  checkpoints: [
    { key: "hospital", label: "Hospital" },
    { key: "floor", label: "Floor" },
    { key: "building", label: "Building" },
  ],
  devices: [
    { key: "hospital", label: "Hospital" },
    { key: "status", label: "Status" },
    { key: "app_version", label: "App version" },
  ],
  guards: [
    { key: "hospital", label: "Hospital" },
    { key: "role", label: "Role" },
  ],
  officers: [
    { key: "hospital", label: "Hospital" },
    { key: "status", label: "Status" },
  ],
};

/** Sources that support time-bucket grouping (have a meaningful date column). */
const TIME_SOURCES: SourceKey[] = ["scans", "hospitals"];

export function dimsForSource(source: SourceKey): DimOpt[] {
  const time = TIME_SOURCES.includes(source) ? TIME_DIMS : [];
  return [...CATEGORICAL_DIMS[source], ...time];
}

export function metricFormat(source: SourceKey, metric: string): MetricOpt["format"] {
  return METRICS_BY_SOURCE[source]?.find((m) => m.key === metric)?.format || "int";
}

export const SOURCE_LABELS: Record<SourceKey, string> = {
  scans: "Scans",
  hospitals: "Hospitals",
  checkpoints: "Checkpoints",
  devices: "Devices",
  guards: "Guards",
  officers: "Officers",
};

// ---------------------------------------------------------------------------
// Value formatting.
// ---------------------------------------------------------------------------
export function formatValue(value: number, fmt: MetricOpt["format"]): string {
  if (value == null || isNaN(value)) return "—";
  switch (fmt) {
    case "decimal":
      return value.toLocaleString("en-US", { maximumFractionDigits: 1 });
    default:
      return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
}

export function prettyLabel(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Data Source Panel catalog. Clicking an item adds a pre-configured widget.
// ---------------------------------------------------------------------------
export interface CatalogItem {
  label: string;
  type: WidgetType;
  source: SourceKey;
  metric: string;
  groupBy?: string | null;
}

export interface CatalogGroup {
  id: string;
  label: string;
  items: CatalogItem[];
}

export const DATA_CATALOG: CatalogGroup[] = [
  {
    id: "scans",
    label: "Scans",
    items: [
      { label: "Total scan count", type: "metric", source: "scans", metric: "count" },
      { label: "Scans by hospital", type: "bar", source: "scans", metric: "count", groupBy: "hospital" },
      { label: "Scans by floor", type: "bar", source: "scans", metric: "count", groupBy: "floor" },
      { label: "Scans by scanner", type: "table", source: "scans", metric: "count", groupBy: "scanner" },
      { label: "Scans over time", type: "line", source: "scans", metric: "count", groupBy: "day" },
      { label: "Scans by day of week", type: "day_of_week", source: "scans", metric: "count", groupBy: "day_of_week" },
      { label: "Scans by hour", type: "bar", source: "scans", metric: "count", groupBy: "hour" },
      { label: "Daily average scans", type: "metric", source: "scans", metric: "daily_average" },
    ],
  },
  {
    id: "hospitals",
    label: "Hospitals",
    items: [
      { label: "Active hospitals", type: "metric", source: "hospitals", metric: "count" },
      { label: "Deployments by month", type: "line", source: "hospitals", metric: "count", groupBy: "month" },
    ],
  },
  {
    id: "checkpoints",
    label: "Checkpoints",
    items: [
      { label: "Total checkpoints", type: "metric", source: "checkpoints", metric: "count" },
      { label: "Checkpoints by hospital", type: "bar", source: "checkpoints", metric: "count", groupBy: "hospital" },
      { label: "Checkpoints by floor", type: "bar", source: "checkpoints", metric: "count", groupBy: "floor" },
      { label: "Scanned checkpoints", type: "metric", source: "checkpoints", metric: "scanned" },
      { label: "Unscanned checkpoints", type: "metric", source: "checkpoints", metric: "unscanned" },
    ],
  },
  {
    id: "devices",
    label: "Devices",
    items: [
      { label: "Active scanners", type: "metric", source: "devices", metric: "active" },
      { label: "Devices by hospital", type: "bar", source: "devices", metric: "count", groupBy: "hospital" },
      { label: "Devices by app version", type: "pie", source: "devices", metric: "count", groupBy: "app_version" },
    ],
  },
  {
    id: "guards",
    label: "Guards",
    items: [
      { label: "Total guards", type: "metric", source: "guards", metric: "count" },
      { label: "Guards by hospital", type: "bar", source: "guards", metric: "count", groupBy: "hospital" },
      { label: "Guards by role", type: "pie", source: "guards", metric: "count", groupBy: "role" },
    ],
  },
  {
    id: "officer_activity",
    label: "Officer Activity",
    items: [
      { label: "Officer-attributed scans", type: "metric", source: "scans", metric: "attributed" },
      { label: "Unattributed scans", type: "metric", source: "scans", metric: "unattributed" },
      { label: "Scans by officer", type: "bar", source: "scans", metric: "count", groupBy: "officer" },
      { label: "Attributed scans over time", type: "line", source: "scans", metric: "attributed", groupBy: "day" },
      { label: "Officers on roster", type: "metric", source: "officers", metric: "count" },
      { label: "Officers by status", type: "pie", source: "officers", metric: "count", groupBy: "status" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Widget factory.
// ---------------------------------------------------------------------------
let counter = 0;
export function uid(): string {
  counter += 1;
  return `w${Date.now().toString(36)}${counter}`;
}

const TYPE_DEFAULT_TITLE: Record<WidgetType, string> = {
  metric: "Metric",
  kpi_row: "Key metrics",
  bar: "Bar chart",
  line: "Trend",
  pie: "Breakdown",
  table: "Data table",
  text: "Text",
  day_of_week: "By day of week",
  comparison: "Comparison",
  status_table: "Ranked table",
  image: "Image",
  ai_summary: "AI summary",
};

const TYPE_DEFAULT_SIZE: Record<WidgetType, WidgetSize> = {
  metric: "small",
  kpi_row: "large",
  bar: "medium",
  line: "large",
  pie: "medium",
  table: "medium",
  text: "large",
  day_of_week: "medium",
  comparison: "medium",
  status_table: "medium",
  image: "medium",
  ai_summary: "large",
};

export function makeWidget(type: WidgetType, overrides: Partial<WidgetConfig> = {}): WidgetConfig {
  const base: WidgetConfig = {
    id: uid(),
    type,
    title: TYPE_DEFAULT_TITLE[type],
    size: TYPE_DEFAULT_SIZE[type],
    source: "scans",
    metric: "count",
    groupBy:
      type === "metric" || type === "ai_summary" || type === "text" || type === "image" ? null : "hospital",
    filters: {},
    sort: "value_desc",
    limit: 10,
    orientation: "vertical",
    donut: type === "pie",
    color: "blue",
    showValues: true,
    text:
      type === "text"
        ? "Double-click to edit this text block. Use it for headers, observations, or recommendations."
        : "",
    imageUrl: null,
    caption: "",
    metrics:
      type === "kpi_row"
        ? [
            // "Verified scans" was dropped from the defaults (it read as a
            // confusing 0 on the Deployment Status Report's "At a glance"
            // row); Daily average keeps the row at four balanced tiles.
            { label: "Total scans", source: "scans", metric: "count" },
            { label: "Daily average", source: "scans", metric: "daily_average" },
            { label: "Active devices", source: "devices", metric: "active" },
            { label: "Checkpoints", source: "checkpoints", metric: "count" },
          ]
        : [],
    seriesB: null,
  };

  if (type === "day_of_week") base.groupBy = "day_of_week";
  if (type === "status_table") {
    base.source = "checkpoints";
    base.groupBy = "hospital";
  }
  if (type === "comparison") {
    base.source = "scans";
    base.metric = "count";
    base.groupBy = "hospital";
    base.seriesB = { label: "Verified", source: "scans", metric: "verified" };
  }

  return { ...base, ...overrides };
}

export function widgetFromCatalog(item: CatalogItem): WidgetConfig {
  return makeWidget(item.type, {
    title: item.label,
    source: item.source,
    metric: item.metric,
    groupBy: item.groupBy ?? null,
    donut: item.type === "pie",
  });
}

/** Build a real widget from an AI assistant suggestion (already server-sanitized). */
export function widgetFromAi(spec: {
  type: WidgetType;
  title?: string;
  source?: SourceKey;
  metric?: string;
  groupBy?: string | null;
  size?: WidgetSize;
  color?: ColorKey;
}): WidgetConfig {
  const overrides: Partial<WidgetConfig> = { donut: spec.type === "pie" };
  if (spec.title) overrides.title = spec.title;
  if (spec.source) overrides.source = spec.source;
  if (spec.metric) overrides.metric = spec.metric;
  if (spec.groupBy !== undefined) overrides.groupBy = spec.groupBy;
  if (spec.size) overrides.size = spec.size;
  if (spec.color) overrides.color = spec.color;
  return makeWidget(spec.type, overrides);
}

// ---------------------------------------------------------------------------
// Starter templates.
// ---------------------------------------------------------------------------
export interface TemplateDef {
  id: string;
  name: string;
  description: string;
  // eslint-disable-next-line no-unused-vars
  build: (author: string) => ReportConfig;
}

function baseRange(): { start?: string; end?: string } {
  return {};
}

export const TEMPLATES: TemplateDef[] = [
  {
    id: "blank",
    name: "Blank Canvas",
    description: "Start from scratch and add your own widgets.",
    build: (author) => ({
      title: "Untitled Report",
      subtitle: "",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [],
    }),
  },
  {
    id: "deployment",
    name: "Deployment Status Report",
    description: "Scan counts by hospital, checkpoint coverage, and deployment timeline.",
    build: (author) => ({
      title: "Deployment Status Report",
      subtitle: "Fleet deployment and checkpoint coverage overview",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [
        makeWidget("text", {
          title: "Header",
          text: "## Deployment Status\nA snapshot of scan activity, checkpoint coverage, and device deployment across hospitals for the reporting period.",
        }),
        makeWidget("kpi_row", { title: "At a glance" }),
        makeWidget("status_table", {
          title: "Checkpoints by hospital",
          source: "checkpoints",
          groupBy: "hospital",
          metric: "count",
          size: "large",
        }),
        makeWidget("bar", {
          title: "Scans by hospital",
          source: "scans",
          metric: "count",
          groupBy: "hospital",
          color: "blue",
        }),
        makeWidget("line", {
          title: "Hospital deployments by month",
          source: "hospitals",
          metric: "count",
          groupBy: "month",
          color: "indigo",
        }),
        makeWidget("ai_summary", { title: "Observations & recommendations" }),
      ],
    }),
  },
  {
    id: "daily",
    name: "Daily Patrol Activity",
    description: "Scan trends, peak hours, and active devices.",
    build: (author) => ({
      title: "Daily Patrol Activity",
      subtitle: "Scan activity and device utilization",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [
        makeWidget("kpi_row", {
          title: "Activity snapshot",
          metrics: [
            { label: "Total scans", source: "scans", metric: "count" },
            { label: "Verified", source: "scans", metric: "verified" },
            { label: "Daily average", source: "scans", metric: "daily_average" },
            { label: "Active devices", source: "devices", metric: "active" },
          ],
        }),
        makeWidget("line", {
          title: "Scans over time",
          source: "scans",
          metric: "count",
          groupBy: "day",
          color: "blue",
        }),
        makeWidget("bar", {
          title: "Scans by hour (peak hours)",
          source: "scans",
          metric: "count",
          groupBy: "hour",
          color: "cyan",
          limit: 24,
        }),
        makeWidget("day_of_week", {
          title: "Activity by day of week",
          source: "scans",
          metric: "count",
          groupBy: "day_of_week",
          color: "indigo",
        }),
        makeWidget("ai_summary", { title: "Summary" }),
      ],
    }),
  },
  {
    id: "comparison",
    name: "Hospital Comparison",
    description: "Side-by-side hospital metrics for scans and coverage.",
    build: (author) => ({
      title: "Hospital Comparison",
      subtitle: "Scan volume and checkpoint coverage by hospital",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [
        makeWidget("bar", {
          title: "Scans by hospital",
          source: "scans",
          metric: "count",
          groupBy: "hospital",
          color: "blue",
          limit: 20,
        }),
        makeWidget("comparison", {
          title: "Checkpoints: total vs scanned",
          source: "checkpoints",
          metric: "count",
          groupBy: "hospital",
          seriesB: { label: "Scanned", source: "checkpoints", metric: "scanned" },
        }),
        makeWidget("table", {
          title: "Guards by hospital",
          source: "guards",
          metric: "count",
          groupBy: "hospital",
          limit: 20,
        }),
        makeWidget("table", {
          title: "Devices by hospital",
          source: "devices",
          metric: "count",
          groupBy: "hospital",
          limit: 20,
        }),
      ],
    }),
  },
  {
    id: "coverage",
    name: "Checkpoint Coverage",
    description: "Scanned vs unscanned checkpoints by hospital and floor.",
    build: (author) => ({
      title: "Checkpoint Coverage Report",
      subtitle: "Scanned vs total checkpoints",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [
        makeWidget("kpi_row", {
          title: "Coverage",
          metrics: [
            { label: "Checkpoints", source: "checkpoints", metric: "count" },
            { label: "Scanned", source: "checkpoints", metric: "scanned" },
            { label: "Unscanned", source: "checkpoints", metric: "unscanned" },
            { label: "Hospitals", source: "hospitals", metric: "count" },
          ],
        }),
        makeWidget("comparison", {
          title: "Total vs scanned by hospital",
          source: "checkpoints",
          metric: "count",
          groupBy: "hospital",
          seriesB: { label: "Scanned", source: "checkpoints", metric: "scanned" },
        }),
        makeWidget("bar", {
          title: "Checkpoints by floor",
          source: "checkpoints",
          metric: "count",
          groupBy: "floor",
          color: "indigo",
          limit: 20,
        }),
        makeWidget("ai_summary", { title: "Observations" }),
      ],
    }),
  },
  {
    id: "devices",
    name: "Device Fleet Status",
    description: "Scanner health, app versions, and activity.",
    build: (author) => ({
      title: "Device Fleet Status",
      subtitle: "Scanner fleet health and versions",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [
        makeWidget("kpi_row", {
          title: "Fleet",
          metrics: [
            { label: "Total devices", source: "devices", metric: "count" },
            { label: "Active", source: "devices", metric: "active" },
            { label: "Total scans", source: "scans", metric: "count" },
            { label: "Hospitals", source: "hospitals", metric: "count" },
          ],
        }),
        makeWidget("pie", {
          title: "Devices by app version",
          source: "devices",
          metric: "count",
          groupBy: "app_version",
          donut: true,
        }),
        makeWidget("bar", {
          title: "Devices by hospital",
          source: "devices",
          metric: "count",
          groupBy: "hospital",
          color: "blue",
        }),
        makeWidget("table", {
          title: "Scans by scanner",
          source: "scans",
          metric: "count",
          groupBy: "scanner",
          limit: 20,
        }),
      ],
    }),
  },
  {
    id: "officer_activity",
    name: "Officer Activity Report",
    description: "Recorded scan activity by officer, attribution rates, and roster status.",
    build: (author) => ({
      title: "Officer Activity Report",
      subtitle: "Recorded scan activity by officer for the reporting period",
      author,
      dateRange: baseRange(),
      includeLogo: true,
      widgets: [
        makeWidget("text", {
          title: "About this report",
          text: "## Officer Activity\nRecorded scan activity attributed to signed-on officers. Scans made while no officer was signed on are counted as unattributed, and duties that produce no scans do not appear. These figures inform supervisor judgement; they are not by themselves a measure of officer performance.",
        }),
        makeWidget("kpi_row", {
          title: "At a glance",
          metrics: [
            { label: "Attributed scans", source: "scans", metric: "attributed" },
            { label: "Unattributed scans", source: "scans", metric: "unattributed" },
            { label: "Officers on roster", source: "officers", metric: "count" },
            { label: "Active officers", source: "officers", metric: "active" },
          ],
        }),
        makeWidget("bar", {
          title: "Scans by officer",
          source: "scans",
          metric: "count",
          groupBy: "officer",
          color: "blue",
        }),
        makeWidget("line", {
          title: "Attributed scans over time",
          source: "scans",
          metric: "attributed",
          groupBy: "day",
          color: "indigo",
        }),
        makeWidget("table", {
          title: "Scan counts by officer",
          source: "scans",
          metric: "count",
          groupBy: "officer",
          limit: 25,
        }),
        makeWidget("ai_summary", { title: "Observations" }),
      ],
    }),
  },
];
